import * as React from 'react';
import { Link } from 'react-router-dom';
import { logout } from 'app/shell/logout';
import { routeConstants, UserProfileSectionType } from 'app/routes/route-constants';
import { HeaderProps, MenuAccess } from '../../types';
import { DropdownMenu } from 'ui';
import {
  MdEqualizer as MdCharts, MdAdd, MdAccountCircle as MdUser,
  MdSearch, MdBuild, MdArrowDropDown, MdArrowRight, MdOutlineAutoAwesome
} from 'react-icons/md';
import { isValidUrl } from 'app/infrastructure/validation/url';
import { MdTrendingUp } from 'react-icons/md';
import { Link as ExternalLink } from 'ui';
import { urlToSurveyManager } from 'app/area/favourite/components/url-to-survey-manager';
import { shouldShowToolsMenu } from 'app/shell/should-show-tools-menu';
import { getAiChatBaseUrl, getSctwLink } from 'app/infrastructure/hosting';
import { urlToAiChat } from 'app/infrastructure/js/url-to-ai-chat';
import styles from './desktop-nav.scss';

import type { JSX } from "react";

const DropDownButton = ({ text, Icon }) => text ? (<div className={styles.hasMaxWidth}>
  <Icon className={styles.navItemIcon} />
  <span className={styles.navItemText}>{text}</span>
  <MdArrowDropDown className="dropdown-arrow" />
</div>) : null;

const Search = props => <li className={styles.navItem}>
  <Link to={routeConstants.createFavouriteDraft}>
    <MdSearch id="nav-search-icon" className={styles.navItemIcon} />
    <span className="visuallyhidden">{props.translation.search}</span>
  </Link>
</li>;

const AiChat = props => 
  props.aiChatAssistantAccessInfo?.aiAdvisor && 
  isValidUrl(urlToAiChat(getAiChatBaseUrl(), props.instanceInfo)) && <li className={styles.navItem}>
    <Link to={urlToAiChat(getAiChatBaseUrl(), props.instanceInfo)} 
      target="_blank" rel="noreferrer" title={props.translation.ai_chat_hover_text}>
      <MdOutlineAutoAwesome id="nav-ai-chat-icon" className={styles.navItemIcon} />
      <span className="visuallyhidden">{props.translation.ai_chat_title}</span>
    </Link>
  </li>;

// eslint:disable:max-func-body-length
const AuthorizedUserProfileItems = ({
  translation,
  helpUrl = undefined,
  feedbackUrl = undefined
}) => (<>
  <li>
    <Link to={routeConstants.userProfileActiveSection(UserProfileSectionType.Settings)} className="dropdown-item">
      {translation.user_profile}
    </Link>
  </li>

  <li>
    <Link to={routeConstants.myCaseDraftsView} className="dropdown-item">
      {translation.my_case_drafts_title}
    </Link>
  </li>

  <li>
    <Link to={routeConstants.myDownloadsView} className="dropdown-item">
      {translation.my_downloads_title}
    </Link>
  </li>

  {isValidUrl(helpUrl) &&
    <li>
      <ExternalLink url={helpUrl} linkText={translation.help} className={'dropdown-item'} />
    </li>}

  <li>
    <Link to={routeConstants.messagesToUserView} className="dropdown-item">
      {translation.messages}
    </Link>
  </li>

  {isValidUrl(feedbackUrl) &&
    <li>
      <ExternalLink url={feedbackUrl} linkText={translation.feedback} className={'dropdown-item'} />
    </li>
  }

  <li>
    <button id="nav-logout-button" className="dropdown-item" type="button" onClick={logout}>
      {translation.logout}
    </button>
  </li>
</>);

const onParentClick = (e: React.MouseEvent<HTMLElement>) => e.stopPropagation();
const AuthorizedToolsItems: (props:
  {
    menuAccess: MenuAccess; translation: any;
    manageSurveysUrl?: string;
    onRefreshCache: (event: React.MouseEvent) => void;
    onRegenerateAggregatedEnv: (event: React.MouseEvent) => void;
    onRegenerateAggregatedRisk: (event: React.MouseEvent) => void;
  }
) => JSX.Element = ({
  menuAccess,
  manageSurveysUrl,
  translation,
  onRefreshCache,
  onRegenerateAggregatedEnv,
  onRegenerateAggregatedRisk
}) => (<>

  {menuAccess.codetableAdministration &&
    <li>
      <Link to={routeConstants.selectCodetableAdministration} className="dropdown-item">
        {translation.codetable_administration}
      </Link>
    </li>}

  {menuAccess.userAdministration &&
    <li>
      <Link to={routeConstants.userSearchView} className="dropdown-item">
        {translation.user_administration}
      </Link>
    </li>}
  {menuAccess.roleAdministration &&
    <li>
      <Link to={routeConstants.rolesView} className="dropdown-item">
        {translation.role_administration}
      </Link>
    </li>}

  {(menuAccess.dashboardAdministration || menuAccess.companyReportAdministration || menuAccess.favouriteAdministration)
    && <li className={styles.nestedDropdown}>
      <div className="dropdown-item" onClick={onParentClick} tabIndex={0}>
        <span>{translation.dashboards_and_reports}</span>
        <div className={styles.icon}>
          <MdArrowRight className={styles.navItemIcon} />
        </div>
      </div>
      <ul className={`dropdown-menu ${styles.subMenu}`}>
        {menuAccess.dashboardAdministration &&
          <li>
            <Link to={routeConstants.dashboardAdminList} className="dropdown-item">
              {translation.manage_dashboard}
            </Link>
          </li>
        }
        {menuAccess.companyReportAdministration &&
          <li>
            <Link to={routeConstants.companyReportAdminList} className="dropdown-item">
              {translation.reports_company}
            </Link>
          </li>}
        {menuAccess.favouriteAdministration &&
          <li>
            <Link to={routeConstants.favouriteAdminList} className="dropdown-item">
              {translation.manage_favourites}
            </Link>
          </li>
        }
      </ul>
    </li>}

  {menuAccess.importSetsAdministration
    && <li className={styles.nestedDropdown}>
      <div className="dropdown-item" onClick={onParentClick} tabIndex={0}>
        <span>{translation.code_mapping_administration}</span>
        <div className={styles.icon}>
          <MdArrowRight className={styles.navItemIcon} />
        </div>
      </div>
      <ul className={`dropdown-menu ${styles.subMenu}`}>
        <li>
          <Link to={routeConstants.importSetList} className="dropdown-item">
            {translation.manage_import_sets}
          </Link>
        </li>
        <li>
          <Link to={routeConstants.importMappings} className="dropdown-item">
            {translation.manage_code_mappings}
          </Link>
        </li>
      </ul>
    </li>}

  {menuAccess.experienceTransferAdministration
    && <li className={styles.nestedDropdown}>
      <div className="dropdown-item" onClick={onParentClick} tabIndex={0}>
        <span>{translation.experience_transfer_administration}</span>
        <div className={styles.icon}>
          <MdArrowRight className={styles.navItemIcon} />
        </div>
      </div>
      <ul className={`dropdown-menu ${styles.subMenu}`}>
        <li>
          <Link to={routeConstants.subscriberGroupsList} className="dropdown-item">
            {translation.subscriber_groups_administration}
          </Link>
        </li>
        <li>
          <Link to={routeConstants.subscriberGroupsUserMembers} className="dropdown-item">
            {translation.user_members_administration}
          </Link>
        </li>
        <li>
          <Link to={routeConstants.experienceTransferList} className="dropdown-item">
            {translation.experience_transfer_configuration}
          </Link>
        </li>
        <li>
          <Link to={routeConstants.experienceTransferOperations} className="dropdown-item">
            {translation.configure_operations_transfer}
          </Link>
        </li>
      </ul>
    </li>}

  {menuAccess.messageAdministration &&
    <li>
      <Link to={routeConstants.administrationMessagesList} className="dropdown-item">
        {translation.messages_to_users}
      </Link>
    </li>}

  {menuAccess.externalUrlAdministration
    && <li className={styles.nestedDropdown}>
      <div className="dropdown-item" onClick={onParentClick} tabIndex={0}>
        <span>{translation.external_urls}</span>
        <div className={styles.icon}>
          <MdArrowRight className={styles.navItemIcon} />
        </div>
      </div>
      <ul className={`dropdown-menu ${styles.subMenu}`}>
        <li>
          <Link to={routeConstants.externalUrlList} className="dropdown-item">
            {translation.external_url}
          </Link>
        </li>
        <li>
          <Link to={routeConstants.externalUrlGroupsList} className="dropdown-item">
            {translation.external_url_groups}
          </Link>
        </li>
      </ul>
    </li>}

  {menuAccess.manageSurveys && manageSurveysUrl && isValidUrl(urlToSurveyManager(manageSurveysUrl)) &&
    <ExternalLink url={urlToSurveyManager(manageSurveysUrl)} linkText={translation.manage_surveys} className={'dropdown-item'} />}

  {menuAccess.imports && <li>
    <Link to={routeConstants.importView} className="dropdown-item">
      {translation.import_title}
    </Link>
  </li>}

  {menuAccess.textConfiguration
    && <li className={styles.nestedDropdown}>
      <div className="dropdown-item" onClick={onParentClick} tabIndex={0}>
        <span>{translation.translations}</span>
        <div className={styles.icon}>
          <MdArrowRight className={styles.navItemIcon} />
        </div>
      </div>
      <ul className={`dropdown-menu ${styles.subMenu}`}>
        <li>
          <Link to={routeConstants.textConfigurationList} className="dropdown-item">
            {translation.texts}
          </Link>
        </li>
        <li>
          <Link to={routeConstants.codeDescriptionList} className="dropdown-item">
            {translation.code_descriptions}
          </Link>
        </li>
      </ul>
    </li>}

  {(menuAccess.refreshCache || menuAccess.regenerateAggregatedData || menuAccess.regenerateAggregatedData)
    && <li className={styles.nestedDropdown}>
      <div className="dropdown-item" onClick={onParentClick} tabIndex={0}>
        <span>{translation.other_tools}</span>
        <div className={styles.icon}>
          <MdArrowRight className={styles.navItemIcon} />
        </div>
      </div>
      <ul className={`dropdown-menu ${styles.subMenu}`}>
        {menuAccess.refreshCache && <li>
          <button id="nav-refresh-cache-button" className="dropdown-item"
            type="button" onClick={onRefreshCache}>
            {translation.refresh_cache_title}
          </button>
        </li>}

        {menuAccess.regenerateAggregatedData && <li>
          <button id="nav-regenerate-env-data-button" className="dropdown-item"
            type="button" onClick={onRegenerateAggregatedEnv}>
            {translation.regenerate_aggregated_environmental_data}
          </button>
        </li>}

        {menuAccess.regenerateAggregatedData && <li>
          <button id="nav-regenerate-risk-data-button" className="dropdown-item"
            type="button" onClick={onRegenerateAggregatedRisk}>
            {translation.regenerate_aggregated_risk_data}
          </button>
        </li>}

        {getSctwLink() && isValidUrl(getSctwLink()) && <li>
          <ExternalLink url={getSctwLink()} linkText={translation.system_configuration_tool} className={'dropdown-item'} />
        </li>}
      </ul>
    </li>}
</>);

const Tools = ({ menuAccess, children, translation }) =>
  shouldShowToolsMenu(menuAccess) && <li className={`${styles.navItem} ${styles.dropdownLi}`}>
    <DropdownMenu
      dropdownNode={<DropDownButton Icon={MdBuild} text={translation.tools} />} closeAfterClick>
      {children}
    </DropdownMenu>
  </li>;

const UserProfile = ({ userInfo = undefined, children }) => userInfo ? (<li className={`${styles.navItem} ${styles.dropdownLi}`}>
  <DropdownMenu
    dropdownNode={<DropDownButton Icon={MdUser} text={userInfo.name} />} closeAfterClick>
    {children}
  </DropdownMenu>
</li>) : null;

const AddNewCase = ({ translation }) => (<li className={`${styles.btnNewCase} ${styles.hasMaxWidth}`}>
  <Link to={routeConstants.selectCaseType} className="btn btn-outline-secondary">
    <MdAdd /> {translation.new_case}
  </Link>
</li>);

const AuthorizedMenuItems = ({ translation }) => (<>
  <li className={styles.navItem}>
    <DropdownMenu dropdownNode={<DropDownButton Icon={MdCharts} text={translation.dashboards} />} closeAfterClick>
      <li>
        <Link to={routeConstants.selectDashboard} className="dropdown-item">
          {translation.dashboards}
        </Link>
      </li>
      <li>
        <Link to={routeConstants.selectSavedDashboards} className="dropdown-item">
          {translation.saved_dashboards}
        </Link>
      </li>
    </DropdownMenu>
  </li>
  <li className={styles.navItem}>
    <DropdownMenu dropdownNode={<DropDownButton Icon={MdTrendingUp} text={translation.reports} />} closeAfterClick>
      <li>
        <Link to={routeConstants.favouriteDraftNew} className="dropdown-item">
          {translation.new_report}
        </Link>
      </li>
      <li>
        <Link to={routeConstants.selectSavedReports} className="dropdown-item">
          {translation.reports_saved}
        </Link>
      </li>
      <li>
        <Link to={routeConstants.selectCompanyReports} className="dropdown-item">
          {translation.reports_company}
        </Link>
      </li>
      <li>
        <Link to={routeConstants.selectSavedCompanyReports} className="dropdown-item">
          {translation.saved_reports_company}
        </Link>
      </li>
    </DropdownMenu>
  </li>
</>);

/**
 * Navigation menu for large screens
 *
 * @param props
 */
export const DesktopNav: React.FunctionComponent<HeaderProps> = (props: HeaderProps) => {
  return (
    <ul className={`navbar-nav me-auto ${styles.itemsContainer} ${styles.desktopNav}`}>
      <AddNewCase {...props} />
      <AuthorizedMenuItems {...props} />
      <Tools {...props}>
        <AuthorizedToolsItems {...props} />
      </Tools>
      <UserProfile {...props}>
        <AuthorizedUserProfileItems {...props} />
      </UserProfile>
      <AiChat {...props} />
      <Search {...props} />
    </ul>
  );
};
